// src/api.js
// Talking to the backend. apiFetch is fetch() plus the signed-in user's Firebase ID token, so the
// server can check who is calling; apiRequest builds on it for the usual JSON calls.
import { API_URL } from './config';
import { getAuthHeaders } from './auth';
import { localEngineEnabled } from './engine/engineFlag';
import { guestActive } from './guest';
import { diag } from './diagnostics';

// Always the server (with the token).
export async function serverFetch(url, options = {}) {
    const authHeaders = await getAuthHeaders();
    return fetch(url, { ...options, headers: { ...options.headers, ...authHeaders } });
}

// Always the engine on this device (the user is whoever holds the device: no token to send).
export async function localFetch(url, options = {}) {
    const engine = await import('./engine/localEngine');
    return engine.localFetch(url, options);
}

// What the app uses: the engine on this device when the device works offline, the server otherwise.
export async function apiFetch(url, options = {}) {
    if (!localEngineEnabled()) return serverFetch(url, options);
    try {
        return await localFetch(url, options);
    } catch (error) {
        // An engine that cannot run on this device must not leave a signed-in person stuck: the server answers instead. (A guest
        // has nobody else to ask.)
        if (error && error.engineUnavailable && !guestActive()) {
            diag('warn', `the offline engine is not available, asking the server instead: ${error.message}`);
            return serverFetch(url, options);
        }
        throw error;
    }
}

// JSON call to `${API_URL}${path}`. GETs carry userId in the query string, everything else in the body.
// Resolves with the parsed response; rejects with an Error carrying the server's message.
export function apiRequest(method, path, userId, body) {
    return requestVia(apiFetch, method, path, userId, body);
}

// The same, through a chosen fetch function (serverFetch or localFetch).
export async function requestVia(fetcher, method, path, userId, body) {
    const isGet = method === 'GET';
    // GETs carry the userId in the query string (after any query the path already has)
    const userQuery = `${path.includes('?') ? '&' : '?'}userId=${encodeURIComponent(userId)}`;
    const url = `${API_URL}${path}${isGet ? userQuery : ''}`;
    const response = await fetcher(
        url,
        isGet
            ? undefined
            : {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId, ...body }),
            }
    );
    if (!response.ok) {
        const details = await response.json().catch(() => ({}));
        const error = new Error(details.error || `${method} ${path} failed (${response.status})`);
        error.status = response.status;
        error.details = details;
        throw error;
    }
    return response.json();
}
